import React from "react";
import PlatformModal from "components/layout/PlatformModal";

const APP_VERSION = "1.0.0";
const APP_YEAR = new Date().getFullYear();
const APP_WEBSITE = "https://fxbazzar.com";

/** Core modules shipped in the mobile trading terminal */
const FEATURES = [
  {
    icon: "📈",
    label: "Market Watch",
    desc: "Live bid, ask & LTP with personal watchlists across segments",
  },
  {
    icon: "🕯️",
    label: "Charts",
    desc: "Candles, line, bars & area — multi-timeframe OHLC with volume",
  },
  {
    icon: "⚡",
    label: "Trade & Orders",
    desc: "Quick order entry, trade book, order book & live positions",
  },
  {
    icon: "💼",
    label: "Wallet",
    desc: "Add funds, withdraw, bank payout & account statement",
  },
  {
    icon: "📊",
    label: "Reports",
    desc: "Ledger, trade report, trial balance & audit logs",
  },
  {
    icon: "🔐",
    label: "Accounts",
    desc: "Switch trading accounts; view-password read-only mode",
  },
];

/** Segments and instruments supported on the platform */
const MARKETS = [
  "NSE Futures",
  "NSE Options",
  "MCX Commodities",
  "Index (NIFTY / BANKNIFTY / SENSEX)",
  "GIFT NIFTY",
];

/** What this mobile build connects to */
const PLATFORM_STACK = [
  "React 18",
  "Redux Toolkit",
  "Lightweight Charts",
  "Socket.IO live feed",
  "FastTrade REST API",
];

const PLATFORM_NOTES = [
  "Synced with the FxBazzar web terminal — same login, positions, and wallet.",
  "Realtime quotes via Socket.IO; orders, reports, and ledger via secure REST API.",
  "Chart preferences, color templates, and dark/light theme saved on your device.",
];

export default function AboutProgramModal({ onClose, darkTheme = false }) {
  return (
    <PlatformModal
      onClose={onClose}
      darkTheme={darkTheme}
      boxClassName="platform-about-modal"
      boxStyle={{ maxWidth: 480 }}
    >
      <div className="platform-about">
        <div className="platform-about__hero">
          <button
            type="button"
            className="platform-about__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
          <div className="platform-about__brand">
            <div className="platform-about__logo" aria-hidden>
              <span>FX</span>
            </div>
            <div className="platform-about__brand-text">
              <h2 className="platform-about__title">FxBazzar</h2>
              <p className="platform-about__subtitle">Mobile Trading Terminal</p>
            </div>
          </div>
          <span className="platform-about__version-badge">Version {APP_VERSION}</span>
        </div>

        <div className="platform-about__body">
          <p className="platform-about__tagline">
            FxBazzar is a professional trading workspace for Indian derivatives and
            commodities — built for traders who need live markets, fast order entry,
            wallet management, and full account reporting on mobile.
          </p>

          <div className="platform-about__section">
            <h3 className="platform-about__section-title">What you can do</h3>
            <ul className="platform-about__features">
              {FEATURES.map((f) => (
                <li key={f.label} className="platform-about__feature">
                  <span className="platform-about__feature-icon" aria-hidden>{f.icon}</span>
                  <span className="platform-about__feature-text">
                    <strong>{f.label}</strong>
                    <span>{f.desc}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="platform-about__section">
            <h3 className="platform-about__section-title">Markets & segments</h3>
            <div className="platform-about__tags">
              {MARKETS.map((tag) => (
                <span key={tag} className="platform-about__tag">{tag}</span>
              ))}
            </div>
          </div>

          <div className="platform-about__section">
            <h3 className="platform-about__section-title">Platform</h3>
            <ul className="platform-about__notes">
              {PLATFORM_NOTES.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </div>

          <div className="platform-about__section">
            <h3 className="platform-about__section-title">Technology</h3>
            <div className="platform-about__tags">
              {PLATFORM_STACK.map((tag) => (
                <span key={tag} className="platform-about__tag">{tag}</span>
              ))}
            </div>
          </div>

          <p className="platform-about__disclaimer">
            Trading in futures, options, and commodities involves substantial risk of loss.
            Only trade with capital you can afford to lose and within your broker&apos;s risk limits.
          </p>

          <div className="platform-about__meta">
            <span>
              Website:{" "}
              <a
                href={APP_WEBSITE}
                target="_blank"
                rel="noopener noreferrer"
                className="platform-about__link"
              >
                fxbazzar.com
              </a>
            </span>
            <span>Live market data via Socket.IO · Account data via FastTrade API</span>
            <span className="platform-about__copyright">
              © {APP_YEAR} FxBazzar. All rights reserved.
            </span>
          </div>
        </div>

        <div className="platform-about__footer">
          <button type="button" className="btn-confirm platform-about__done" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </PlatformModal>
  );
}
